import NiceModal from '@ebay/nice-modal-react';

import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import Button from 'components/field/Button';
import Alert from 'components/global/Alert';
import Icon from 'components/global/Icon';
import Image from 'components/global/Image';
import Typography from 'components/global/Typography';

import useLocale from 'hooks/useLocale';

import { getOTOPackageList } from 'pages/OTOModules/OTOPackageListSlice';
import SubscriptionAccountModal from 'pages/Settings/Payment/SubscriptionAccount/SubscriptionAccountModal';

import { faBox } from '@fortawesome/pro-regular-svg-icons';

import * as S from './SavingsCalculator.styles';

const PlanComparison = ({ savingsCalculatorData, billingPeriod }) => {
	const dispatch = useDispatch();
	const { t } = useTranslation();

	const user = useSelector((state) => state.user);
	const { Country } = useLocale();
	const { otoPackageList, isLoading } = useSelector((state) => state.otoPackageList);

	useEffect(() => {
		dispatch(getOTOPackageList());
	}, [dispatch]);

	const currentPlan = useMemo(
		() => otoPackageList.find((p) => p.planName === user.planName),
		[otoPackageList, user.planName]
	);

	const filteredPlans = useMemo(() => {
		if (savingsCalculatorData) {
			return savingsCalculatorData.map((plan) => ({
				...plan,
				upgradeButtonLabel: otoPackageList.find((p) => p.planName === plan.planName)?.upgradeButtonLabel,
				basePriceWithoutDiscount:
					billingPeriod === 'yearly' ? plan.basePriceWithoutDiscount / 12 : plan.basePriceWithoutDiscount,
				basePrice: billingPeriod === 'yearly' ? plan.basePrice / 12 : plan.basePrice
			}));
		}

		return otoPackageList
			.filter((plan) => plan.planName !== user.planName && currentPlan.tier < plan.tier && plan.tier < 4)
			.map((plan) => ({
				...plan,
				basePriceWithoutDiscount:
					billingPeriod === 'yearly'
						? plan.yearlyPriceWithoutDiscount / 12
						: plan.monthlyPriceWithoutDiscount,
				basePrice: billingPeriod === 'yearly' ? plan.yearlyPrice / 12 : plan.monthlyPrice,
				additionalFeatures: plan.planName === 'StarterPackage' ? '+15' : '+20'
			}));
	}, [savingsCalculatorData, otoPackageList, billingPeriod, user.planName]);

	if (isLoading) {
		return (
			<S.PlanComparisonContainer>
				<Typography variant="p2" fontWeight="700">
					planComparison
				</Typography>
				<Typography variant="p3" color="var(--content-3)">
					loadingPlans
				</Typography>
			</S.PlanComparisonContainer>
		);
	}
	const alertContentByCountry = {
		AE: t('planComparisonAlertAE'),
		TR: t('planComparisonAlertTR'),
		SA: t('planComparisonAlertSA')
	};

	return (
		<S.PlanComparisonContainer>
			<S.PlanComparisonCard>
				<S.CardHeader>
					<Typography variant="p2" fontWeight="700">
						planComparison
					</Typography>
					<Typography variant="p3" color="var(--content-3)">
						planComparisonSubtitle
					</Typography>
				</S.CardHeader>
				{savingsCalculatorData && (
					<Alert variant="info" showIcon className="my-3" content={alertContentByCountry[Country]} />
				)}
				<S.PlansWrapper>
					{filteredPlans.map((plan) => (
						<PlanCard
							key={plan.planName}
							plan={plan}
							otoPackageList={otoPackageList}
							billingPeriod={billingPeriod}
							user={user}
						/>
					))}
				</S.PlansWrapper>
			</S.PlanComparisonCard>
		</S.PlanComparisonContainer>
	);
};

const PlanCard = ({ plan, otoPackageList, billingPeriod, user }) => {
	const { t } = useTranslation();

	const packageIcon = useMemo(
		() => otoPackageList.find((storedPlan) => storedPlan.planName === plan.planName)?.packageIcon,
		[otoPackageList, plan.planName]
	);

	const handleClick = useCallback(
		(plan) => {
			NiceModal.show(SubscriptionAccountModal, {
				currentPackage: otoPackageList.find((item) => item.planName === user.nextPackageName),
				selectedPackage: plan,
				selectedPlanType: billingPeriod
			});
		},
		[otoPackageList, user.nextPackageName, billingPeriod]
	);

	return (
		<S.PlanCard key={plan.planName} isRecommended={plan.isRecommended || false}>
			{plan.isRecommended && (
				<S.CornerTag>
					<Typography variant="caption" fontWeight="500" color="var(--alwaysWhiteColor)">
						recommended
					</Typography>
				</S.CornerTag>
			)}
			<S.ImageAndBadgeWrapper>
				<Image url={packageIcon} alt={plan.planName} width="9rem" height="9rem" />
				{plan.totalSavingsRate > 0 && (
					<div>
						<S.DiscountBadge>
							<Typography fontWeight="500" variant="p3" color="var(--forest)">
								{plan.totalSavingsRate || 0} {t('savings')}
							</Typography>
						</S.DiscountBadge>
					</div>
				)}
			</S.ImageAndBadgeWrapper>
			<Typography variant="p1" fontWeight="500">
				{plan.planName}
			</Typography>
			<S.InfoLine>
				<Typography variant="p3" color="var(--content-2)" fontWeight="700">
					{billingPeriod === 'monthly' ? 'monthlySavings' : 'yearlySavings'}
				</Typography>
				<Typography variant="h3" withCurrency color="var(--forest)" fontWeight="700">
					{plan.totalSavingsAmount || 0}
				</Typography>
			</S.InfoLine>
			{plan.freeShipments > 0 && (
				<S.GreenAlert>
					<Icon icon={faBox} color="var(--forest)" size="medium" />
					<Typography variant="p3" fontWeight="700" color="var(--forest)">
						{plan.freeShipments || 0} {t('additionalShipmentsPossible')}
					</Typography>
				</S.GreenAlert>
			)}
			<S.Divider />
			<S.InfoLine alignCenter>
				<Typography variant="p3" fontWeight="500">
					basePriceOfAPlan
				</Typography>
				<S.DiscountedPriceContainer>
					{plan.basePriceWithoutDiscount > 0 && (
						<Typography variant="p2" fontWeight="500" withCurrency component="s" color="var(--red)">
							{plan.basePriceWithoutDiscount || 0}
						</Typography>
					)}
					<div style={{ display: 'flex' }}>
						<Typography variant="p2" fontWeight="700" withCurrency>
							{plan.basePrice || 0}
						</Typography>
						<Typography variant="p2" fontWeight="700">
							/{t('monthSort')}
						</Typography>
					</div>
				</S.DiscountedPriceContainer>
			</S.InfoLine>
			<S.InfoLine>
				<Typography variant="p3" fontWeight="500">
					shippingDiscount
				</Typography>
				<Typography variant="p2" fontWeight="700">
					{plan.shippingFeeDiscountPercentage || '0%'}
				</Typography>
			</S.InfoLine>
			<S.InfoLine>
				<Typography variant="p3" fontWeight="500">
					additionalFeatures
				</Typography>
				<Typography variant="p2" fontWeight="700">
					{plan.additionalFeatures || '0'}
				</Typography>
			</S.InfoLine>
			<S.InfoLine>
				<Typography variant="p3" fontWeight="500">
					monthlyFreeShipment
				</Typography>
				<Typography variant="p2" fontWeight="700">
					{plan.monthlyFreeIntegratorOrder || '0'}
				</Typography>
			</S.InfoLine>
			<Typography variant="caption" color="var(--content-disabled)">
				({t('yourOwnRates')})
			</Typography>

			<Button
				variant={plan.isRecommended ? 'blue' : 'outline'}
				label={plan.upgradeButtonLabel}
				fullWidth
				className="mt-5"
				onClick={() => handleClick(plan)}
			/>
		</S.PlanCard>
	);
};

export default PlanComparison;
